import { useState } from 'react';
import { useRef } from 'react';

import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import FeatherIcon from 'feather-icons-react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { ValidationAlert } from 'components/FormComponents';
import FadeInMotion from 'components/Motion/FadeInMotion';

import styles from './Input.module.scss';

export const Input = ({
  name,
  type = 'text',
  autoComplete,
  placeholder,
  defaultValue,
  min,
  max,
  step,
  minLength,
  maxLength,
  validators = {},
  icon = '',
  actionIcon,
  actionIconTip = '',
  register = () => {},
  error = '',
  noErrorMessage,
  disabled,
  className,
  onActionClick = () => {},
  onBlur = () => {},
  inputProps = {},
  newIconLibrary = false,
  dataTestId,
  noPadding = false,
  readOnly = false,
}) => {
  const [passwordRevealed, setPasswordRevealed] = useState(false);
  const inputRef = useRef(null);

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(styles.root, 'd-flex align-items-center u-gap flex-grow-1', {
          [className]: className,
          [styles.rootWithError]: error && !noErrorMessage,
          'u-padding-0': noPadding,
        })}>
        {icon && <FeatherIcon className={styles.icon} icon={icon} />}

        <input
          ref={inputRef}
          name={name}
          type={passwordRevealed ? 'text' : type}
          autoComplete={autoComplete}
          placeholder={placeholder}
          defaultValue={defaultValue}
          min={min}
          max={max}
          step={step}
          minLength={minLength}
          maxLength={maxLength}
          {...register(name, validators)}
          onBlur={e => onBlur(e.target.value)}
          onWheel={e => (type === 'number' ? e.target.blur() : undefined)}
          disabled={disabled}
          readOnly={readOnly}
          data-testid={dataTestId}
          className={cn(styles.input, {
            [styles.invalid]: !!error,
            [styles.inputWithIcon]: !!icon,
            [styles.disabled]: disabled,
            [styles.readOnly]: readOnly,
          })}
          {...inputProps}
        />

        {type === 'password' && (
          <span
            className={cn(styles.showPassword, 'u-link', 'dark')}
            onMouseDown={() => setPasswordRevealed(true)}
            onMouseUp={() => setPasswordRevealed(false)}
            onMouseLeave={() => setPasswordRevealed(false)}>
            Show
          </span>
        )}

        {actionIcon &&
          (newIconLibrary ? (
            actionIconTip ? (
              <OverlayTrigger
                trigger={['hover', 'focus']}
                placement="bottom"
                overlay={<Tooltip id={`input-${name}-tooltip`}>{actionIconTip}</Tooltip>}>
                <span className="d-inline-block">
                  <HugeiconsIcon
                    icon={actionIcon}
                    className="text-neutral-6 cursor-pointer"
                    size={20}
                    onClick={() => onActionClick(inputRef.current?.value)}
                  />
                </span>
              </OverlayTrigger>
            ) : (
              <HugeiconsIcon
                icon={actionIcon}
                className="text-neutral-6 cursor-pointer"
                size={20}
                onClick={() => onActionClick(inputRef.current?.value)}
              />
            )
          ) : (
            <FeatherIcon
              icon={actionIcon}
              className={styles.actionButton}
              onClick={onActionClick}
            />
          ))}
      </div>
      {error && !noErrorMessage && (
        <FadeInMotion key={name}>
          <ValidationAlert show={true} message={error} />
        </FadeInMotion>
      )}
    </div>
  );
};

Input.propTypes = {
  name: PropTypes.string,
  type: PropTypes.oneOf([
    'text',
    'short_text',
    'long_text',
    'number',
    'date',
    'password',
    'email',
    'tel',
    'url',
    'website',
  ]),
  autoComplete: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  validators: PropTypes.object,
  icon: PropTypes.string,
  actionIcon: PropTypes.string,
  register: PropTypes.func,
  error: PropTypes.string,
  noErrorMessage: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onActionClick: PropTypes.func,
  inputProps: PropTypes.object,
};
